import { Box } from '@mui/material';
import { GridColumns } from '@mui/x-data-grid';
import React, { memo, useMemo } from 'react';
import {
  StyledCheckbox,
  StyledCollapseTableCell,
  StyledCollapseTableChild,
} from './CollapseTableRow.styles';
import useShiftKeyDetector from './hook';
import styled from 'styled-components';
import { GridPinnedColumns } from '@mui/x-data-grid-pro';

type Props = {
  columns: GridColumns;
  row: any;
  checkboxSelection?: boolean;
  value: any;
  onRowClick?: (row: any) => void;
  selectedItems?: string[];
  setSelectedItems?: (value: string[]) => void;
  pinnedColumns?: GridPinnedColumns;
};

const PinnedColumnLeft = styled(Box)({
  position: 'sticky',
  left: 0,
  backgroundColor: '#fff',
  zIndex: 2,
  display: 'flex',
  '& > *:last-child': {
    borderRight: '2px solid rgba(224, 224, 224, 1)',
  },
});

const PinnedColumnRight = styled(Box)({
  position: 'sticky',
  right: 0,
  backgroundColor: '#fff',
  zIndex: 2,
  display: 'flex',
  '& > *:first-child': {
    borderRight: '2px solid rgba(224, 224, 224, 1)',
  },
});

const ScrollContainer = styled(Box)({
  display: 'flex',
  position: 'relative',
  flex: 1,
});

const CollapseTableChild: React.FC<Props> = memo(
  ({
    columns,
    row,
    value,
    checkboxSelection,
    onRowClick,
    selectedItems,
    setSelectedItems,
    pinnedColumns,
  }) => {
    const checked = useMemo(() => !!selectedItems?.includes(value?.id), [selectedItems, value]);
    const isShiftPressed = useShiftKeyDetector();

    const onCheckItem = (event: React.ChangeEvent<HTMLInputElement>) => {
      if (!setSelectedItems) return;
      if (checked && !event.target.checked && selectedItems && selectedItems?.length > 0) {
        setSelectedItems(selectedItems?.filter((item) => item !== value?.id));
      } else if (isShiftPressed && selectedItems?.length) {
        const selectedItemIndex = row.indexOf(row.find((rowItem: any) => rowItem.id === value.id));
        const formatedSelectedItems = selectedItems.map((item) => ({
          id: item,
          index: row.indexOf(row.find((rowItem: any) => rowItem.id === item)),
        }));
        const lastItem = formatedSelectedItems.reduce(
          (max, obj) => (obj.index > max.index ? obj : max),
          formatedSelectedItems[0],
        );
        const firstItem = formatedSelectedItems.reduce(
          (min, obj) => (obj.index < min.index ? obj : min),
          formatedSelectedItems[0],
        );
        const closestFirstItem =
          formatedSelectedItems
            .filter((item) => item.index < selectedItemIndex)
            .reduce<{ id: string; index: any } | undefined>(
              (prev, curr) =>
                prev === undefined ||
                selectedItemIndex - curr.index < selectedItemIndex - prev.index
                  ? curr
                  : prev,
              undefined,
            ) || null;

        if (selectedItemIndex < firstItem.index) {
          const minSelectedItems = row
            .filter((_: any, index: any) => index >= selectedItemIndex && index < firstItem.index)
            .map((item: any) => item.id);
          setSelectedItems([...minSelectedItems, ...(selectedItems ?? [])]);
        } else if (selectedItemIndex > lastItem.index) {
          const maxSelectedItems = row
            .filter((_: any, index: any) => index <= selectedItemIndex && index > lastItem.index)
            .map((item: any) => item.id);
          setSelectedItems([...maxSelectedItems, ...(selectedItems ?? [])]);
        } else if (closestFirstItem) {
          const middleSelectedItems = row
            .filter(
              (_: any, index: any) => index > closestFirstItem.index && index <= selectedItemIndex,
            )
            .map((item: any) => item.id);
          setSelectedItems([...middleSelectedItems, ...(selectedItems ?? [])]);
        }
      } else {
        setSelectedItems([value?.id, ...(selectedItems ?? [])]);
      }
    };

    const renderCell = (column: any) => (
      <StyledCollapseTableCell
        maxWidth={column?.maxWidth}
        minWidth={column?.minWidth}
        width={column?.width}
        textAlign={column?.align}
        flex={column?.flex}
        key={column.field}
      >
        {column?.renderCell({
          value: column?.field === 'preview' ? value.url : value[column?.field],
          row,
        })}
      </StyledCollapseTableCell>
    );

    const hasPinnedCheckbox = pinnedColumns?.left?.some((col) => col.includes('check'));

    return (
      <StyledCollapseTableChild sx={{ display: 'flex' }} onClick={onRowClick}>
        {checkboxSelection && !hasPinnedCheckbox && (
          <StyledCheckbox checked={checked} onChange={onCheckItem} />
        )}
        {/* Left pinned columns */}
        {pinnedColumns?.left && (
          <PinnedColumnLeft>
            {checkboxSelection && hasPinnedCheckbox && (
              <StyledCheckbox checked={checked} onChange={onCheckItem} />
            )}
            {columns
              .filter((col) => pinnedColumns.left?.includes(col.field))
              .map((column) => renderCell(column))}
          </PinnedColumnLeft>
        )}

        {/* Center scrollable columns */}
        <ScrollContainer>
          {columns
            .filter(
              (col) =>
                !pinnedColumns?.left?.includes(col.field) &&
                !pinnedColumns?.right?.includes(col.field),
            )
            .map((column) => renderCell(column))}
        </ScrollContainer>

        {/* Right pinned columns */}
        {pinnedColumns?.right && (
          <PinnedColumnRight>
            {columns
              .filter((col) => pinnedColumns.right?.includes(col.field))
              .map((column) => renderCell(column))}
          </PinnedColumnRight>
        )}
      </StyledCollapseTableChild>
    );
  },
);

export default CollapseTableChild;
